import React, { useState, useEffect } from 'react';
import NetworkStack from './NetworkStack';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

const NetworkDemoPage = () => {
    const authenticatedFetch = useAuthenticatedFetch();
    const [viewMode, setViewMode] = useState('3d'); // '3d' or '2d'
    const [selectedItem, setSelectedItem] = useState(null);

    // Live Data State
    const [locations, setLocations] = useState([]);
    const [racks, setRacks] = useState([]);
    const [devices, setDevices] = useState([]);

    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [selectedRackId, setSelectedRackId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Initial Load: Locations
    useEffect(() => {
        authenticatedFetch('/api/network/locations')
            .then(res => res.json())
            .then(data => {
                const arr = Array.isArray(data) ? data : [];
                setLocations(arr);
                if (arr.length > 0) setSelectedLocationId(arr[0].id);
            })
            .catch(err => console.error(err));
    }, [authenticatedFetch]);

    // Load Racks when Location changes
    useEffect(() => {
        if (!selectedLocationId) return;
        authenticatedFetch(`/api/network/racks?locationId=${selectedLocationId}`)
            .then(res => res.json())
            .then(data => {
                setRacks(data);
                if (data.length > 0) setSelectedRackId(data[0].id);
            });
    }, [authenticatedFetch, selectedLocationId]);

    // Load Devices when Rack changes
    useEffect(() => {
        if (!selectedRackId) return;
        setLoading(true);
        authenticatedFetch(`/api/network/devices?rackId=${selectedRackId}`)
            .then(res => res.json())
            .then(data => {
                setDevices(data);
                setLoading(false);
            });
    }, [authenticatedFetch, selectedRackId]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Critical': return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', dot: 'bg-red-400' };
            case 'Legacy': return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', dot: 'bg-amber-400' };
            case 'Warning': return { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', dot: 'bg-yellow-400' };
            case 'Active': return { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' };
            default: return { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', dot: 'bg-slate-400' };
        }
    };

    return (
        <div className="w-full h-screen relative bg-black overflow-hidden">
            {/* TCB Brand Mark — bottom-right corner */}
            <div className="absolute bottom-5 right-6 z-10 flex items-center gap-3 pointer-events-none">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/20"></div>
                <img src="/images/tcb-logo.png" alt="" className="h-6 w-6 opacity-40" draggable="false" />
                <div className="flex flex-col">
                    <img
                        src="/images/MJG_Logo.png"
                        alt="McJunkins Group"
                        className="h-3.5 w-auto object-contain brightness-0 invert opacity-70"
                        draggable="false"
                    />
                    <span className="text-[8px] text-white/20 tracking-[0.2em] uppercase leading-tight">Digital Twin</span>
                </div>
            </div>

            {/* Controls Overlay */}
            <div className="absolute top-6 left-4 z-10 flex flex-col gap-4 w-64">
                {/* View Mode Toggle */}
                <div className="bg-[#0a0a0a]/90 backdrop-blur-xl rounded-2xl border border-white/5 p-1 flex">
                    <button
                        onClick={() => setViewMode('3d')}
                        className={`flex-1 py-2 px-3 rounded-xl text-[10px] font-bold tracking-widest uppercase transition-all ${viewMode === '3d'
                            ? 'bg-white/10 text-white'
                            : 'text-white/30 hover:text-white/50'
                            }`}
                    >
                        3D View
                    </button>
                    <button
                        onClick={() => setViewMode('2d')}
                        className={`flex-1 py-2 px-3 rounded-xl text-[10px] font-bold tracking-widest uppercase transition-all ${viewMode === '2d'
                            ? 'bg-white/10 text-white'
                            : 'text-white/30 hover:text-white/50'
                            }`}
                    >
                        2D View
                    </button>
                </div>

                {/* Location Select */}
                <div className="bg-[#0a0a0a]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/5">
                    <label className="text-[10px] text-white/30 uppercase font-bold tracking-widest mb-2 block">Location</label>
                    <select
                        className="w-full bg-[#111] text-white/80 p-2 rounded-lg border border-white/10 outline-none text-sm focus:border-white/20 transition-colors"
                        style={{ colorScheme: 'dark' }}
                        value={selectedLocationId || ''}
                        onChange={e => setSelectedLocationId(Number(e.target.value))}
                    >
                        {locations.map(l => (
                            <option key={l.id} value={l.id} style={{ background: '#1a1a1a', color: '#e5e5e5' }}>{l.name}</option>
                        ))}
                    </select>
                </div>

                {/* Rack Select */}
                {racks.length > 0 && (
                    <div className="bg-[#0a0a0a]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/5">
                        <label className="text-[10px] text-white/30 uppercase font-bold tracking-widest mb-2 block">Rack Cabinet</label>
                        <select
                            className="w-full bg-[#111] text-white/80 p-2 rounded-lg border border-white/10 outline-none text-sm focus:border-white/20 transition-colors"
                            style={{ colorScheme: 'dark' }}
                            value={selectedRackId || ''}
                            onChange={e => setSelectedRackId(Number(e.target.value))}
                        >
                            {racks.map(r => (
                                <option key={r.id} value={r.id} style={{ background: '#1a1a1a', color: '#e5e5e5' }}>{r.name}</option>
                            ))}
                        </select>
                        <div className="mt-2 text-[10px] text-white/20 tracking-wider uppercase">
                            {devices.length} Devices detected
                        </div>
                    </div>
                )}
            </div>

            {/* Exit Button */}
            <a href="/" className="absolute top-6 right-6 z-10 text-white/30 hover:text-white transition-colors bg-[#0a0a0a]/80 backdrop-blur-xl px-4 py-2 rounded-full border border-white/5 hover:border-white/15 text-xs font-medium tracking-wider uppercase">
                Exit View
            </a>

            {/* === 3D VIEW === */}
            {viewMode === '3d' && (
                <>
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center text-white/20 font-mono animate-pulse text-sm tracking-widest">Loading Geometry...</div>
                    ) : (
                        <NetworkStack
                            items={devices}
                            onBladeClick={setSelectedItem}
                            authenticatedFetch={authenticatedFetch}
                        />
                    )}
                </>
            )}

            {/* === 2D LIST VIEW === */}
            {viewMode === '2d' && (
                <div className="absolute inset-0 overflow-y-auto" style={{ background: 'linear-gradient(180deg, #080808 0%, #0a0a0a 40%, #0d0d0d 100%)' }}>
                    <div className="mx-4 mt-48 mb-24 md:ml-72 md:mr-8 md:mt-6">
                        {loading ? (
                            <div className="flex items-center justify-center h-64 text-white/20 font-mono animate-pulse text-sm tracking-widest">Loading...</div>
                        ) : devices.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-white/15">
                                <div className="text-4xl mb-3 opacity-30">⬡</div>
                                <p className="text-sm tracking-wider uppercase">No devices in this rack</p>
                            </div>
                        ) : (
                            <div className="space-y-0">
                                {/* Table Header (Hidden on Mobile) */}
                                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/5">
                                    <div className="col-span-2 text-[10px] text-white/20 font-bold tracking-widest uppercase">Type</div>
                                    <div className="col-span-3 text-[10px] text-white/20 font-bold tracking-widest uppercase">Model</div>
                                    <div className="col-span-3 text-[10px] text-white/20 font-bold tracking-widest uppercase">Status</div>
                                    <div className="col-span-4 text-[10px] text-white/20 font-bold tracking-widest uppercase">Details</div>
                                </div>

                                {/* Device Rows */}
                                <div className="space-y-4 md:space-y-0">
                                    {[...devices].sort((a, b) => (b.position_u || 0) - (a.position_u || 0)).map((device, idx) => {
                                        const sc = getStatusColor(device.status);
                                        return (
                                            <div
                                                key={device.id}
                                                onClick={() => setSelectedItem(device)}
                                                className="flex flex-col md:grid md:grid-cols-12 gap-3 md:gap-4 px-4 md:px-6 py-4 md:py-4 rounded-xl md:rounded-none border border-white/[0.04] md:border-0 md:border-b md:border-white/[0.03] bg-white/[0.02] md:bg-transparent hover:bg-white/[0.04] cursor-pointer transition-colors group"
                                                style={{ animationDelay: `${idx * 30}ms` }}
                                            >
                                                {/* Header / Type / Status (Mobile Top Row) */}
                                                <div className="flex justify-between items-center md:hidden">
                                                    <span className="text-xs font-bold text-white/60 tracking-wider uppercase">{device.type}</span>
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sc.bg} ${sc.text} border ${sc.border}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                                                        {device.status || 'Unknown'}
                                                    </span>
                                                </div>

                                                {/* Type (Desktop) */}
                                                <div className="hidden md:flex col-span-2 items-center">
                                                    <span className="text-sm text-white/60">{device.type}</span>
                                                </div>

                                                {/* Model */}
                                                <div className="md:col-span-3 flex items-center">
                                                    <span className="text-base md:text-sm font-medium text-white/90 md:text-white/80 group-hover:text-white transition-colors">{device.model_name || '—'}</span>
                                                </div>

                                                {/* Status (Desktop) */}
                                                <div className="hidden md:flex col-span-3 items-center">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sc.bg} ${sc.text} border ${sc.border}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                                                        {device.status || 'Unknown'}
                                                    </span>
                                                </div>

                                                {/* Details */}
                                                <div className="md:col-span-4 flex flex-col md:flex-row items-start md:items-center pt-2 md:pt-0 border-t border-white/[0.04] md:border-0 gap-2">
                                                    {device.specs && Object.keys(device.specs).length > 0 ? (
                                                        <div className="flex gap-2 flex-wrap">
                                                            {Object.entries(device.specs)
                                                                .filter(([key, val]) => key !== 'portBanks' && key !== 'notes' && key !== 'upgrade')
                                                                .slice(0, 3)
                                                                .map(([key, val]) => (
                                                                    <span key={key} className="text-[10px] text-white/30 md:text-white/20 bg-white/[0.05] md:bg-white/[0.03] px-2 py-0.5 rounded">
                                                                        {key}: {String(val)}
                                                                    </span>
                                                                ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-white/10 hidden md:block">—</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* === READ-ONLY DEVICE INFO PANEL === */}
            {selectedItem && (() => {
                const specs = typeof selectedItem.specs === 'string'
                    ? JSON.parse(selectedItem.specs || '{}')
                    : (selectedItem.specs || {});
                const sc = getStatusColor(selectedItem.status);
                const portBanks = specs.portBanks || [];
                return (
                    <div className="fixed bottom-4 left-4 right-4 md:bottom-auto md:left-auto md:top-16 md:right-4 md:w-[340px] z-50">
                        <div className="bg-[#0d0d0d]/98 backdrop-blur-2xl rounded-2xl border border-white/[0.06] shadow-2xl shadow-black/80 md:shadow-black/60 overflow-hidden max-h-[80vh] flex flex-col">
                            {/* Header */}
                            <div className="px-5 pt-5 pb-4 border-b border-white/[0.04]">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-white/[0.06] text-white/40 border border-white/[0.08] mb-2">{selectedItem.type}</span>
                                        <h2 className="text-lg font-bold text-white truncate">{selectedItem.model_name || selectedItem.name}</h2>
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest mt-2 ${sc.bg} ${sc.text} border ${sc.border}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                                            {selectedItem.status}
                                        </span>
                                    </div>
                                    <button onClick={() => setSelectedItem(null)} className="ml-3 text-white/20 hover:text-white/60 transition-colors flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="px-5 py-4 space-y-3 overflow-y-auto min-h-0 flex-1 custom-scrollbar">
                                {/* Position / Height / Type */}
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.03]">
                                        <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Position</span>
                                        <span className="text-white/60 font-mono text-sm">U{selectedItem.position_u}</span>
                                    </div>
                                    <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.03]">
                                        <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Height</span>
                                        <span className="text-white/60 font-mono text-sm">{selectedItem.height_u}U</span>
                                    </div>
                                    <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.03]">
                                        <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Type</span>
                                        <span className="text-white/60 text-sm">{selectedItem.type}</span>
                                    </div>
                                </div>

                                {/* Port Banks */}
                                {portBanks.length > 0 && (
                                    <div className="bg-white/[0.02] rounded-xl border border-white/[0.04] p-4">
                                        <h3 className="text-[10px] font-bold text-white/20 uppercase tracking-widest mb-3">Ports</h3>
                                        <div className="space-y-1.5">
                                            {portBanks.map((bank, i) => (
                                                <div key={i} className="flex justify-between items-center text-sm">
                                                    <span className="text-white/40">{bank.type}</span>
                                                    <span className="text-white/60 font-mono font-semibold">{bank.count} port{bank.count !== 1 ? 's' : ''}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                {specs.notes && (
                                    <div className="bg-white/[0.02] rounded-xl border border-white/[0.04] p-4">
                                        <h3 className="text-[10px] font-bold text-white/20 uppercase tracking-widest mb-2">Notes</h3>
                                        <p className="text-white/40 text-sm whitespace-pre-wrap leading-relaxed">{specs.notes}</p>
                                    </div>
                                )}

                                {/* Recommended Upgrade */}
                                {specs.upgrade && (
                                    <div className="bg-white/[0.02] rounded-xl border border-blue-500/10 p-4">
                                        <h3 className="text-[10px] font-bold text-blue-400/60 uppercase tracking-widest mb-3">Recommended Upgrade</h3>
                                        <div className="flex justify-between items-start">
                                            <div className="min-w-0 flex-1 mr-3">
                                                <p className="text-white/80 font-semibold text-sm">{specs.upgrade.model}</p>
                                                <p className="text-white/25 text-xs mt-0.5">{specs.upgrade.description}</p>
                                            </div>
                                            <span className="text-emerald-400/80 font-bold font-mono text-sm flex-shrink-0">{specs.upgrade.price}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

        </div>
    );
};

export default NetworkDemoPage;
